import { useEffect, useMemo, useState } from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import HubIcon from '@mui/icons-material/Hub';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  Autocomplete,
  Avatar,
  Button,
  Card,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Skeleton,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material';
import { useOrganizationSelect } from '../../context/OrganizationSelectContext';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganizations } from '../../features/Organization/OrganizationSlice';

const ROOT_PRIORITY = ['TOOLBOX', 'MAYBOX', 'CLOUDBOX'];
const ROOT_TREE_SECTIONS = ['TOOLBOX', 'CLOUDBOX', 'MAYBOX'];
const ORGANIZATION_TYPE_META = {
  client: { label: 'Client', color: 'primary' },
  fournisseur: { label: 'Fournisseur', color: 'warning' },
  partenaire: { label: 'Partenaire', color: 'secondary' },
};

function normalizeRoots(organization) {
  if (Array.isArray(organization?.rootOrganizations) && organization.rootOrganizations.length > 0) {
    return organization.rootOrganizations
      .filter((root) => root?._id)
      .map((root) => ({
        _id: String(root._id),
        name: root.name || 'Sans nom',
      }));
  }

  if (organization?.rootOrganization?._id) {
    return [{
      _id: String(organization.rootOrganization._id),
      name: organization.rootOrganization.name || 'Sans nom',
    }];
  }

  return [];
}

function hasRoot(organization, rootId) {
  return normalizeRoots(organization).some((root) => String(root._id) === String(rootId));
}

function getPrimaryRoot(organization) {
  return normalizeRoots(organization)[0] || null;
}

function getRootNames(organization) {
  return normalizeRoots(organization).map((root) => root.name);
}

function normalizeOrganizationTypes(organization) {
  return [...new Set(
    (organization?.organizationTypes || [])
      .filter(Boolean)
      .map((type) => String(type).toLowerCase())
  )];
}

function getOrganizationTypeLabels(organization) {
  return normalizeOrganizationTypes(organization).map((type) =>
    ORGANIZATION_TYPE_META[type]?.label || type
  );
}

function getStaffLabel(user = {}) {
  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
  return fullName || user.email || 'Staff';
}

function normalizeStaffPortfolio(portfolio = []) {
  return (Array.isArray(portfolio) ? portfolio : [])
    .map((entry, index) => ({
      user: entry.user?._id || entry.user || entry.userId,
      userDetails: entry.user,
      priority: Number(entry.priority || index + 1),
    }))
    .filter((entry) => entry.user)
    .sort((a, b) => a.priority - b.priority)
    .map((entry, index) => ({ ...entry, priority: index + 1 }));
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function sortRoots(a, b) {
  const aName = (a?.name || '').toUpperCase();
  const bName = (b?.name || '').toUpperCase();
  const aIndex = ROOT_PRIORITY.indexOf(aName);
  const bIndex = ROOT_PRIORITY.indexOf(bName);

  if (aIndex !== -1 || bIndex !== -1) {
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  }

  return (a?.name || '').localeCompare(b?.name || '');
}

function buildTree(list) {
  const rootMap = new Map();
  const rootChildrenMap = new Map();
  const nodeMap = new Map();

  list.forEach((org) => {
    nodeMap.set(String(org._id), { ...org, children: [], relatedNodes: [] });
  });

  list.forEach((org) => {
    const node = nodeMap.get(String(org._id));
    const parentId = org?.parentOrganization?._id
      ? String(org.parentOrganization._id)
      : org?.organizationParent
        ? String(org.organizationParent)
        : null;

    if (parentId && nodeMap.has(parentId) && parentId !== String(org._id)) {
      nodeMap.get(parentId).children.push(node);
      return;
    }

    normalizeRoots(org).forEach((root) => {
      const rootId = String(root._id);

      if (!rootMap.has(rootId)) {
        rootMap.set(rootId, {
          _id: rootId,
          name: root.name,
          isVirtualRoot: true,
          children: [],
          relatedNodes: [],
          rootOrganizations: [root],
        });
        rootChildrenMap.set(rootId, new Map());
      }

      if (String(org._id) === rootId) {
        const realNode = nodeMap.get(String(org._id));
        const rootNode = rootMap.get(rootId);

        rootMap.set(rootId, {
          ...realNode,
          _id: rootId,
          name: realNode?.name || rootNode.name,
          isVirtualRoot: true,
          rootOrganizations: rootNode.rootOrganizations || [root],
          relatedNodes: realNode?.relatedNodes || [],
          children: realNode?.children || [],
        });
        return;
      }

      if (!parentId || !nodeMap.has(parentId) || !hasRoot(nodeMap.get(parentId), rootId)) {
        rootChildrenMap.get(rootId).set(String(org._id), node);
      }
    });
  });

  list.forEach((org) => {
    if (!org.organizationRelated) return;
    const relatedId = String(org.organizationRelated);
    if (nodeMap.has(relatedId)) {
      nodeMap.get(relatedId).relatedNodes.push(nodeMap.get(String(org._id)));
    }
  });

  rootMap.forEach((rootNode, rootId) => {
    const fallbackChildren = Array.from(rootChildrenMap.get(rootId)?.values() || []);

    rootNode.children = (rootNode.children && rootNode.children.length > 0
      ? rootNode.children
      : fallbackChildren
    ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  });

  return Array.from(rootMap.values()).sort(sortRoots);
}

function renderRootsChips(organization, keyPrefix = 'root') {
  const roots = normalizeRoots(organization);

  if (roots.length === 0) {
    return <Typography variant="body2" color="text.secondary">Aucune</Typography>;
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {roots.map((root) => (
        <Chip
          key={`${keyPrefix}-${organization?._id || root._id}-${root._id}`}
          size="small"
          color="primary"
          variant="outlined"
          label={root.name}
        />
      ))}
    </Stack>
  );
}

function renderTypeChips(organization, keyPrefix = 'type') {
  const types = normalizeOrganizationTypes(organization);

  if (types.length === 0) {
    return <Typography variant="body2" color="text.secondary">Aucun</Typography>;
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {types.map((type) => {
        const meta = ORGANIZATION_TYPE_META[type] || { label: type, color: 'default' };
        return (
          <Chip
            key={`${keyPrefix}-${organization?._id || 'org'}-${type}`}
            size="small"
            color={meta.color}
            variant="outlined"
            label={meta.label}
          />
        );
      })}
    </Stack>
  );
}

const Organizations = () => {
  const dispatch = useDispatch();
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRefreshFromPipeHudu, setLoadingRefreshFromPipeHudu] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [portfolioSaving, setPortfolioSaving] = useState(false);
  const [editingPortfolioOrg, setEditingPortfolioOrg] = useState(null);
  const [portfolioDraftUsers, setPortfolioDraftUsers] = useState([]);

  const { data: organizations = [] } = useSelector((state) => state.organizations.organizations);
  const { selectedOrganization } = useOrganizationSelect();

  const staffOptions = useMemo(
    () => staffUsers.map((user) => ({ ...user, value: String(user._id), label: getStaffLabel(user) })),
    [staffUsers]
  );

  const staffById = useMemo(() => {
    const map = new Map();
    staffUsers.forEach((user) => map.set(String(user._id), user));
    return map;
  }, [staffUsers]);

  useEffect(() => {
    if (selectedOrganization) {
      setFilteredOrganizations(
        organizations.filter((item) => item.name === selectedOrganization)
      );
      return;
    }

    setFilteredOrganizations(organizations);
  }, [selectedOrganization, organizations]);

  useEffect(() => {
    if (filteredOrganizations && filteredOrganizations.length > 0) {
      setTreeData(buildTree(filteredOrganizations));
      return;
    }

    setTreeData([]);
  }, [filteredOrganizations]);

  useEffect(() => {
    const fetchStaffUsers = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/staff-portfolio/staff-options`,
          { headers: getAuthHeaders() }
        );
        setStaffUsers(Array.isArray(data) ? data : []);
      } catch (error) {
        setStaffUsers([]);
      }
    };

    fetchStaffUsers();
  }, []);

  const createHudufromDb = async (organizationId, organizationName) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/organizations/createHuduFromDB/${organizationId}`
      );

      if (response.status === 200 || response.status === 201) {
        dispatch(fetchOrganizations());
        setAlert({
          type: 'success',
          message: `Création dans Hudu réussie pour l'organisation: ${organizationName}`,
        });
      }
    } catch (error) {
      console.error('Error creating Hudu from DB:', error);
      setAlert({
        type: 'error',
        message: `Erreur lors de la création Hudu pour ${organizationName}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStaffPortfolio = (portfolio = []) => {
    const rows = normalizeStaffPortfolio(portfolio);
    if (!rows.length) return <Typography variant="body2" color="text.secondary">Aucun</Typography>;

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {rows.map((entry) => {
          const user = entry.userDetails || staffById.get(String(entry.user));
          return (
            <Chip
              key={`${entry.user}-${entry.priority}`}
              size="small"
              label={`${entry.priority}. ${getStaffLabel(user || {})}`}
            />
          );
        })}
      </Stack>
    );
  };

  const openPortfolioDialog = (organization) => {
    const selectedUsers = normalizeStaffPortfolio(organization.staffPortfolio)
      .map((entry) => staffOptions.find((option) => option.value === String(entry.user)))
      .filter(Boolean);
    setEditingPortfolioOrg(organization);
    setPortfolioDraftUsers(selectedUsers);
    setPortfolioDialogOpen(true);
  };

  const closePortfolioDialog = () => {
    setPortfolioDialogOpen(false);
    setEditingPortfolioOrg(null);
    setPortfolioDraftUsers([]);
  };

  const savePortfolio = async () => {
    if (!editingPortfolioOrg?._id) return;
    setPortfolioSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/staff-portfolio/organizations/${editingPortfolioOrg._id}`,
        {
          staffPortfolio: portfolioDraftUsers.map((user, index) => ({
            user: user.value || user._id,
            priority: index + 1,
          })),
        },
        { headers: getAuthHeaders() }
      );
      closePortfolioDialog();
      dispatch(fetchOrganizations());
      setAlert({
        type: 'success',
        message: `Portefeuille mis à jour pour ${editingPortfolioOrg.name}`,
      });
    } catch (error) {
      setAlert({
        type: 'error',
        message: error?.response?.data?.message || `Erreur lors de la mise à jour du portefeuille pour ${editingPortfolioOrg.name}`,
      });
    } finally {
      setPortfolioSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'clients',
        columns: [
          {
            accessorFn: (row) => row.name,
            id: 'name',
            header: 'Nom',
            size: 250,
            Cell: ({ renderedCellValue }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {renderedCellValue}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.parentOrganization?.name,
            id: 'parentOrganization',
            header: 'Parent Organization',
            size: 250,
            Cell: ({ renderedCellValue }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {renderedCellValue || '—'}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => getOrganizationTypeLabels(row).join(', '),
            id: 'organizationTypes',
            header: 'Types',
            size: 220,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {renderTypeChips(row.original, 'table-type')}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => getRootNames(row).join(', '),
            id: 'rootOrganization',
            header: 'Root Organization',
            size: 280,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {renderRootsChips(row.original, 'table-root')}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => normalizeStaffPortfolio(row.staffPortfolio)
              .map((entry) => getStaffLabel(entry.userDetails || staffById.get(String(entry.user)) || {}))
              .join(', '),
            id: 'staffPortfolio',
            header: 'Portefeuille client',
            size: 300,
            Cell: ({ row }) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  {renderStaffPortfolio(row.original.staffPortfolio)}
                </Box>
                <Tooltip title="Modifier le portefeuille client">
                  <IconButton size="small" onClick={() => openPortfolioDialog(row.original)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => getPrimaryRoot(row)?.name || '',
            id: 'primaryRootOrganization',
            header: 'Root principale',
            size: 180,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {getPrimaryRoot(row.original)?.name || '—'}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.pipedrive_id ? 'Oui' : 'Non',
            id: 'pipedrive',
            header: 'Pipedrive',
            size: 80,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {row.original.pipedrive_id ? <DoneIcon color="success" /> : <ClearIcon color="error" />}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.hudu_id ? 'Oui' : 'Non',
            id: 'hudu',
            header: 'Hudu',
            size: 120,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {row.original.hudu_id ? (
                  <DoneIcon color="success" />
                ) : (
                  <>
                    <ClearIcon color="error" />
                    {loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => createHudufromDb(row.original._id, row.original.name)}
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.infogerance ? 'Yes' : 'No',
            id: 'infogerance',
            header: 'Infogérance',
            size: 40,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {row.original.infogerance ? <DoneIcon color="success" /> : <ClearIcon color="error" />}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.o365Licenses && row.o365Licenses.length > 0 ? 'Yes' : 'No',
            id: 'o365Licenses',
            header: 'O365',
            size: 40,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {row.original.o365Licenses && row.original.o365Licenses.length > 0 ? (
                  <DoneIcon color="success" />
                ) : (
                  <ClearIcon color="error" />
                )}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.esetLicenses && row.esetLicenses.length > 0 ? 'Yes' : 'No',
            id: 'esetLicenses',
            header: 'ESET',
            size: 40,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {row.original.esetLicenses && row.original.esetLicenses.length > 0 ? (
                  <DoneIcon color="success" />
                ) : (
                  <ClearIcon color="error" />
                )}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.vbo365Licenses && row.vbo365Licenses.length > 0 ? 'Yes' : 'No',
            id: 'vbo365Licenses',
            header: 'Veeam Backup O365',
            size: 40,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {row.original.vbo365Licenses && row.original.vbo365Licenses.length > 0 ? (
                  <DoneIcon color="success" />
                ) : (
                  <ClearIcon color="error" />
                )}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
        ],
      },
    ],
    [dispatch, loading, staffById, staffOptions]
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredOrganizations || [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
    },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
    },
  });

  const treeSections = useMemo(() => {
    const treeMap = new Map(
      treeData.map((node) => [String(node.name || '').toUpperCase(), node])
    );

    return ROOT_TREE_SECTIONS.map((rootName) => ({
      rootName,
      node: treeMap.get(rootName) || null,
    }));
  }, [treeData]);

  const renderTree = (nodes, prefix = '') =>
    nodes.map((node) => {
      const label = (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', py: 0.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {node.isVirtualRoot ? <HubIcon fontSize="small" color="primary" /> : null}
              <Typography fontWeight={node.isVirtualRoot ? 700 : 500}>{node.name}</Typography>
              {node.infogerance ? (
                <Chip size="small" color="success" variant="outlined" label="Infogérance" />
              ) : null}
            </Stack>
            <Box sx={{ mt: 0.75 }}>
              {renderRootsChips(node, `tree-root-${node._id}`)}
            </Box>
            <Box sx={{ mt: 0.75 }}>
              {renderTypeChips(node, `tree-type-${node._id}`)}
            </Box>
          </Box>

          {node.relatedNodes.length > 0 ? (
            <Stack direction="column" spacing={1} sx={{ ml: 4 }}>
              {node.relatedNodes.map((rel) => (
                <Box
                  key={`${rel._id}-related`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.5,
                    borderRadius: 1,
                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                  }}
                >
                  <Chip label="Related" size="small" color="info" />
                  <Typography variant="body2">{rel.name}</Typography>
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
      );

      return (
        <TreeItem
          key={`${prefix}${node._id}`}
          itemId={`${prefix}${node._id}`}
          label={label}
        >
          {node.children.length > 0 ? renderTree(node.children, `${prefix}${node._id}-`) : null}
        </TreeItem>
      );
    });

  const RefreshPipeHudu = async () => {
    try {
      setLoadingRefreshFromPipeHudu(true);
      const syncPipedrive = await axios.get(`${process.env.REACT_APP_API_URL}/api/organizations/syncpipedrive`);

      if (syncPipedrive.status === 200) {
        const GetHuduOrg = await axios.get(`${process.env.REACT_APP_API_URL}/api/organizations/setOrganizationToHudu`);

        if (GetHuduOrg.status === 200) {
          const deduplicate = await axios.get(`${process.env.REACT_APP_API_URL}/api/organizations/deduplicate`);

          if (deduplicate.status === 200) {
            dispatch(fetchOrganizations());
            window.location.reload();
          }
        }
      }
    } catch (error) {
      console.error('Error fetching URLs:', error);
      setAlert({
        type: 'error',
        message: 'Erreur pendant la synchronisation Pipedrive/Hudu',
      });
    } finally {
      setLoadingRefreshFromPipeHudu(false);
    }
  };

  return (
    <Box>
      {filteredOrganizations && filteredOrganizations.length > 0 ? (
        <Box>
          <Typography variant="h6" my={1}>
            Clients
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2} alignContent="center">
              {treeSections.map(({ rootName, node }) => (
                <Grid key={rootName} size={{ xs: 12, md: 4 }}>
                  <Card sx={{ p: 1, mt: 2, borderRadius: 3, height: '100%' }}>
                    <Box display="flex" alignItems="center" my={1} mx={2} columnGap={1}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <DeviceHubIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{rootName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Organigramme root organization
                        </Typography>
                      </Box>
                    </Box>
                    {node ? (
                      <SimpleTreeView
                        slots={{
                          expandIcon: ChevronRight,
                          collapseIcon: ExpandMore,
                        }}
                      >
                        {renderTree([node], `${rootName}-`)}
                      </SimpleTreeView>
                    ) : (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Aucune organisation trouvée pour cette root.
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
              Liste des clients
            </Typography>

            {loadingRefreshFromPipeHudu ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CircularProgress size={30} />
              </Box>
            ) : null}

            {alert ? (
              <Box sx={{ mb: 2 }}>
                <Alert variant="filled" severity={alert.type === 'success' ? 'success' : 'error'}>
                  {alert.message}
                </Alert>
              </Box>
            ) : null}

            <MaterialReactTable table={table} />

            <Dialog open={portfolioDialogOpen} onClose={closePortfolioDialog} fullWidth maxWidth="sm">
              <DialogTitle>
                {`Portefeuille client - ${editingPortfolioOrg?.name || ''}`}
              </DialogTitle>
              <DialogContent dividers>
                <Autocomplete
                  multiple
                  options={staffOptions}
                  value={portfolioDraftUsers}
                  getOptionLabel={(option) => option.label || getStaffLabel(option)}
                  isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                  onChange={(_, value) => setPortfolioDraftUsers(value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        size="small"
                        label={`${index + 1}. ${option.label}`}
                      />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label="Gestionnaires" />}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closePortfolioDialog}>Annuler</Button>
                <Button variant="contained" onClick={savePortfolio} disabled={portfolioSaving}>
                  Enregistrer
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      ) : (
        <Box>
          <Skeleton variant="text" width={260} sx={{ fontSize: '3rem' }} />
          <Skeleton variant="text" width={200} sx={{ fontSize: '2rem' }} />
          <Grid container spacing={2} alignContent="center">
            <Grid size={{ xs: 6, md: 4 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Grid>
          </Grid>
          <Skeleton variant="text" width={200} sx={{ fontSize: '2rem' }} />
          <Skeleton variant="rectangular" width="100%" height={600} />
        </Box>
      )}
    </Box>
  );
};

export default Organizations;
